import React, { FormEvent, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import { NavBar } from "@components/navigation/NavBar/NavBar";
import useMultiStepForm from "@hooks/useMultiStepForm";
import PersonalDetails from "../RAV/PersonalDetails";
import DriverLicence from "./DriverLicence";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import { useUserJourneyContext } from "@context/UserJourneyContext";
import { OnboardingService } from "@services/journeys/Onboarding";
import { Country } from "@utils/constants/localisation";
import { useAPIError } from "@context/ServerErrorContext";
import Warning from "@components/modal/info/Warning";
import ModalContainer from '@components/modal/Container';
import CircularSpinner from "@components/spinners/Circular/Circular";
import FlexiOwnPersonalDetails from "@views/FlexiOwn/FlexiOwnPersonalDetails";
import AddressDetails from "@views/journeys/AddressDetails";
import FlexiOwnEmergencyContacts from "@views/FlexiOwn/FlexiOwnEmergencyContacts";
import AgreementSchedule from "@views/FlexiOwn/AgreementSchedule";
import { FlexiOwnUsedVehicleAddendum, FlexiOwnUsedVehicleAddendumV2 } from "@views/FlexiOwn/UsedVehicleAddendum";
import { NSWEVSubscriptions, NSWEVSubscriptionsFO2 } from "@views/FlexiOwn/NSWEVSubscriptions";
import FlexiPersonalDetails from "@views/Flexi/FlexiPersonalDetails";
import FlexiAgreementSchedule from "@views/Flexi/FlexiAgreementSchedule";
import RAVAgreementSchedule from "@views/RAV/RAVAgreementSchedule";
import { IPBFlexiOwnV1, IPBFlexiOwnV2 } from "@views/FlexiOwn/InsurancePriceBeat";
import { addBeforeUnloadListener, removeBeforeUnloadListener } from "@utils/refreshCatcher";
import RAVShortTermJourneyPersonalDetails from "@views/RAV/RAVShortTermJourneyPersonalDetails";
import { InvoicePreview } from "@views/InvoicePreview/InvoicePreview";
import { PlanOptions } from "@utils/planOptions";
import { ipbCouponChoicesAU, ipbCouponChoicesUK } from "@utils/insurancePriceBeatOptions";
import { SplendInitiative } from "@services/customer/CustomerPotentials";

import breadcrumbObject from "../../SmartOpsHome/breadcrumbObject";
import { createPayload } from "./OnboardingFormPayloadFactory";
import { DynamicPricingAU, DynamicPricingUK } from "@views/Initiatives/DynamicPricing";
import IgnitionView from "@views/Initiatives/IgnitionView";
import UKFOAgreementSchedule from "@views/UKFlexiOwn/UKFlexiOwnAgreementSchedule";
import { OnboardingV2Review, OnboardingReview } from "./OnboardingReview";
import FlexiOwn2AgreementSchedule from "@views/FlexiOwn/FlexiOwn2AgreementSchedule";
import HPCarOnlyDocumentUpload from "../Initiatives/HPCarOnlyDocumentUpload";
import PHAVPersonalDetails from "@views/PHAV/PHAVPersonalDetails";
import DiscountView from "@views/Flexi/DiscountView";


/**
 * Component that puts together the steps for all onboarding
 * journeys into what is a multi step form.
 *
 * Each step is a component that simply exposes some inputs.
 * There is a context that's shared across the entire journey,
 * meant to provide default values to the input fields (where
 * it is possible and justified). If any are updated, then the
 * new value will be stored into the context.
 *
 * When the user reaches the last step (having all mandatory fields
 * filled) and submits the form, the frontend will take the info
 * from the context, send it to the backend and will get the redirect
 * url to the contract signing page, which will be automatically
 * loaded once retrieved.
 *
 * If there will be any errors during the server calls, the error
 * context (that wraps all routes/pages) state will be set, making
 * a modal to be shown. The only option the user will have is an Ok
 * button, which once clicked, the user will be redirected to the
 * homepage, loosing all progress.
 */
export default function OnboardingForm() {

    const { formState: {
        carInfo,
        addressInfo,
        contractDetails,
        driverDetails,
        personalInfo,
        emergencyContactInfo,
        usedVehicleAddendum,
        splendInitiatives,
        selectedPotential,
        cbpOnboard,
    } } = useUserJourneyContext();
    let breadCrumbObject = breadcrumbObject;

    const [loading, setLoading] = useState<Boolean>(false);
    const [stepNames, setStepNames] = useState<string[] | string>('');
    const [steps, setSteps] = useState<any>('');
    const [plan, setPlan] = useState<String[] | String>(['']);
    const [invoicePreviewRequired, setInvoicePreviewRequired] = useState<any>(false);
    const [submitButtonText, setSubmitButtonText] = useState<string>('Next');

    // Given the way of how journey steps are grouped together in lists,
    // there's a constraint on how the review page can be added at the end
    // of a functional journey and still to reflect in real time changes
    // the user makes at predecessor steps. Out of tech reasons (see below),
    // it cannot be part of these lists and make use of the context states.
    // As an alternative, a state will be used to decide whether the page
    // should be displayed, which will have custom set logics with respect
    // to the last step of the journey.
    // The tech reasons: say that all onboarding lists had the Review component
    // as part of them, configured with the context fields passed. It looks
    // like React behaves like it creates some sort of a copy of the context
    // from the moment before entering any of the onboardings first steps and
    // this copy is sent to the Review page. It's not updated as the user makes
    // changes or selections along the journey. To overcome this, the Review
    // page must be rendered dynamically, hence the solution based on the state.
    const [displayReviewPage, setDisplayReviewPage] = useState<boolean>(false);
    const [displayInvoicePreviewPage, setDisplayInvoicePreviewPage] = useState<boolean>(false);

    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const { setErr, removeErr } = useAPIError();
    const navigate = useNavigate();

    const newCountry = driverDetails.country
    // The components included in the RAV journey
    const ravSteps: React.ReactElement[] = [
        <PersonalDetails breadcrumbObject={breadCrumbObject} />,
        <DriverLicence breadcrumbObject={breadCrumbObject} />,
        <RAVAgreementSchedule breadcrumbObject={breadCrumbObject} />,
    ];

    // The names of each component to populate NavBar for RAV journey
    const ravStepNames: string[] = [
        'Personal details',
        'Driver licence',
        'Agreement schedule',
    ];

    const ravShortTermSteps: React.ReactElement[] = [
        <RAVShortTermJourneyPersonalDetails breadcrumbObject={breadCrumbObject} />,
        <AddressDetails />,
        <DriverLicence breadcrumbObject={breadCrumbObject} />,
        <RAVAgreementSchedule breadcrumbObject={breadCrumbObject} />,
    ];

    // The names of each component to populate NavBar for RAV journey
    const ravShortTermStepNames: string[] = [
        'Personal details',
        'Contact details',
        'Driver licence',
        'Agreement schedule',
    ];

    const phavSteps: React.ReactElement[] = [
        <PHAVPersonalDetails/>,
        <AddressDetails />,
        <DriverLicence breadcrumbObject={breadCrumbObject} />,
        <RAVAgreementSchedule breadcrumbObject={breadCrumbObject} />,
    ];

    const phavStepNames: string[] = [
        'Personal details',
        'Contact details',
        'Driver licence',
        'Agreement schedule',
    ];

    /**
     * Flexi Try journey uses the following steps and stepNames for specific Splend Initiative:
     * In AU,
     * - Dynamic Pricing: flexiDynamicSteps and flexiDynamicStepNames
     * - Ignition and others: flexiSteps and flexiStepNames
     * In UK,
     * - Dynamic Pricing: flexiDynamicStepsUK and flexiDynamicStepNamesUK
     * - Ignition and others: flexiIgnitionSteps and flexiIgnitionStepNames
     */
    const flexiSteps: React.ReactElement[] = [  // AU
        <FlexiPersonalDetails breadcrumbObject={breadCrumbObject} />,
        <AddressDetails />,
        <DriverLicence breadcrumbObject={breadCrumbObject} />,
        <FlexiOwnEmergencyContacts />,
        cbpOnboard.PlanId  // Logics to use the appropriate view for the Flexiv2 journey.
        ? <FlexiOwn2AgreementSchedule breadcrumbObject={breadCrumbObject} /> 
        : <FlexiAgreementSchedule breadcrumbObject={breadCrumbObject} />,
    ];
    // The names of each component to populate NavBar for Flexi Own journey
    const flexiStepNames: string[] = [  // AU
        'Personal details',
        'Address details',
        'Driver licence',
        'Emergency contacts',
        'Agreement schedule',
    ];
    // Include new Discount step for Flexi v2 (Ignition and others)
    if (cbpOnboard.PlanId) {
        flexiSteps.push(
            <DiscountView
                splendInitiative={selectedPotential.splend_initiative}
                pricingRecord={cbpOnboard}
            />
        );
        flexiStepNames.push('Discount');
    }
    // Flexi Steps - Dynamic Pricing
    const flexiDynamicSteps: React.ReactElement[] = [  // AU
        <FlexiPersonalDetails breadcrumbObject={breadCrumbObject} />,
        <AddressDetails />,
        <DriverLicence breadcrumbObject={breadCrumbObject} />,
        <FlexiOwnEmergencyContacts />,
        cbpOnboard.PlanId  // Logics to use the appropriate view for the Flexiv2 journey.
        ? <FlexiOwn2AgreementSchedule breadcrumbObject={breadCrumbObject} /> 
        : <FlexiAgreementSchedule breadcrumbObject={breadCrumbObject} />,
        cbpOnboard.PlanId  // Logics to use the appropriate view for the FOv2 journey.
            ? <DiscountView splendInitiative={SplendInitiative.DynamicPricing} pricingRecord={cbpOnboard} />
            : <DynamicPricingAU />,
    ];
    // The names of each component to populate NavBar for Flexi Own journey
    const flexiDynamicStepNames: string[] = [  // AU
        'Personal details',
        'Address details',
        'Driver licence',
        'Emergency contacts',
        'Agreement schedule',
        cbpOnboard.PlanId  // Logics to use the appropriate view for the FOv2 journey.
            ? 'Discount'
            : 'Dynamic Pricing Declaration',
    ];

    const flexiDynamicStepsUK: React.ReactElement[] = [
        <FlexiPersonalDetails breadcrumbObject={breadCrumbObject} />,
        <AddressDetails />,
        <DriverLicence breadcrumbObject={breadCrumbObject} />,
        <FlexiOwnEmergencyContacts />,
        <FlexiAgreementSchedule breadcrumbObject={breadCrumbObject} />,
        <DynamicPricingUK />
    ];
    // The names of each component to populate NavBar for Flexi Own journey
    const flexiDynamicStepNamesUK: string[] = [
        'Personal details',
        'Address details',
        'Driver licence',
        'Emergency contacts',
        'Agreement schedule',
        'Dynamic Pricing Declaration',
    ];

    // Flexi Steps - Ignition - Used only in UK
    const flexiIgnitionSteps: React.ReactElement[] = [  // UK
        <FlexiPersonalDetails breadcrumbObject={breadCrumbObject} />,
        <AddressDetails />,
        <DriverLicence breadcrumbObject={breadCrumbObject} />,
        <FlexiOwnEmergencyContacts />,
        <FlexiAgreementSchedule breadcrumbObject={breadCrumbObject} />,
        <IgnitionView />
    ];
    // The names of each component to populate NavBar for Flexi Own journey
    const flexiIgnitionStepNames: string[] = [ // UK
        'Personal details',
        'Address details',
        'Driver licence',
        'Emergency contacts',
        'Agreement schedule',
        'Splend Ignition programme',
    ];

    const flexiOwnSteps: React.ReactElement[] = [
        <FlexiOwnPersonalDetails breadcrumbObject={breadCrumbObject} />,
        <AddressDetails />,
        <DriverLicence breadcrumbObject={breadCrumbObject} />,
        <FlexiOwnEmergencyContacts />,
        cbpOnboard.PlanId  // Logics to use the appropriate view for the FOv2 journey.
            ? <FlexiOwn2AgreementSchedule breadcrumbObject={breadCrumbObject} /> 
            : <AgreementSchedule breadcrumbObject={breadCrumbObject} />,
        cbpOnboard.PlanId  // Logics to use the appropriate view for the FOv2 journey.
            ? <NSWEVSubscriptionsFO2 />
            : <NSWEVSubscriptions breadcrumbObject={breadCrumbObject} />,
        cbpOnboard.PlanId  // Logics to use the appropriate view for the FOv2 journey.
            ? <IPBFlexiOwnV2 couponChoices={ipbCouponChoicesAU} country={Country.AU} />
            : <IPBFlexiOwnV1 couponChoices={ipbCouponChoicesAU} country={Country.AU} />,
    ];

    // The names of each component to populate NavBar for Flexi Own journey
    const flexiOwnStepNames: string[] = [
        'Personal details',
        'Address details',
        'Driver licence',
        'Emergency contacts',
        'Agreement schedule',
        'NSW EV Addendum',
        'Insurance Price Beat',
    ];

    const flexiOwnUKSteps: React.ReactElement[] = [
        <FlexiOwnPersonalDetails breadcrumbObject={breadCrumbObject} />,
        <AddressDetails />,
        <DriverLicence breadcrumbObject={breadCrumbObject} />,
        <FlexiOwnEmergencyContacts />,
        <UKFOAgreementSchedule breadcrumbObject={breadCrumbObject} />,
        <IPBFlexiOwnV1 couponChoices={ipbCouponChoicesUK} country={Country.GB} />,
    ];

    // The names of each component to populate NavBar for Flexi Own journey
    const flexiOwnUKStepNames: string[] = [
        'Personal details',
        'Address details',
        'Driver licence',
        'Emergency contacts',
        'Agreement schedule',
        'Insurance Price Beat',
    ];

    const flexiOwnUsedSteps: React.ReactElement[] = [
        <FlexiOwnPersonalDetails breadcrumbObject={breadCrumbObject} />,
        <AddressDetails />,
        <DriverLicence breadcrumbObject={breadCrumbObject} />,
        <FlexiOwnEmergencyContacts />,
        cbpOnboard.PlanId  // Logics to use the appropriate view for the FOv2 journey.
            ? <FlexiOwn2AgreementSchedule breadcrumbObject={breadCrumbObject} /> 
            : <AgreementSchedule breadcrumbObject={breadCrumbObject} />,
        cbpOnboard.PlanId
            ? <FlexiOwnUsedVehicleAddendumV2 />
            : <FlexiOwnUsedVehicleAddendum />,
        cbpOnboard.PlanId  // Logics to use the appropriate view for the FOv2 journey.
            ? <NSWEVSubscriptionsFO2 />
            : <NSWEVSubscriptions breadcrumbObject={breadCrumbObject} />,
    ];
    const flexiOwnUsedStepNames: string[] = [
        'Personal details',
        'Address details',
        'Driver licence',
        'Emergency contacts',
        'Agreement schedule',
        'Splend Flexi Own - Used vehicle',
        'NSW EV Addendum',
    ];

    const flexiOwnUsedUKSteps: React.ReactElement[] = [
        <FlexiOwnPersonalDetails breadcrumbObject={breadCrumbObject} />,
        <AddressDetails />,
        <DriverLicence breadcrumbObject={breadCrumbObject} />,
        <FlexiOwnEmergencyContacts />,
        <UKFOAgreementSchedule breadcrumbObject={breadCrumbObject} />,
        <FlexiOwnUsedVehicleAddendum />
    ];
    const flexiOwnUsedUKStepNames: string[] = [
        'Personal details',
        'Address details',
        'Driver licence',
        'Emergency contacts',
        'Agreement schedule',
        'Splend Pre-loved',
    ];

    // HP Car Only Promo journey steps
    const hpCarOnlyPromoSteps: React.ReactElement[] = [
        <HPCarOnlyDocumentUpload />,
        <FlexiOwnPersonalDetails breadcrumbObject={breadCrumbObject} />,
        <AddressDetails />,
        <DriverLicence breadcrumbObject={breadCrumbObject} />,
        <FlexiOwnEmergencyContacts />,
        <UKFOAgreementSchedule breadcrumbObject={breadCrumbObject} />,
        <IPBFlexiOwnV1 couponChoices={ipbCouponChoicesUK} country={Country.GB} />,
    ];

    const hpCarOnlyPromoStepNames: string[] = [
        'Document upload',
        'Personal details',
        'Address details',
        'Driver licence',
        'Emergency contacts',
        'Agreement schedule',
        'Insurance Price Beat',
    ];

    // Assigns set of steps depending on the journey/plan
    const selectJourneyFromPlan = (plan: PlanOptions) => {
        switch (plan) {
            case PlanOptions.RAVCreditHire:
                setSteps(ravSteps);
                setStepNames(ravStepNames);
                break;
            case PlanOptions.RAVSharecover:
                setSteps(ravSteps);
                setStepNames(ravStepNames);
                break;
            case PlanOptions.PHAVCreditHire:
            case PlanOptions.PHAVShortTermRental:
                setSteps(phavSteps);
                setStepNames(phavStepNames);
                break;
            case PlanOptions.RAVShortTermRental:
                setSteps(ravShortTermSteps);
                setStepNames(ravShortTermStepNames);
                break;
            case PlanOptions.SplendFlexi:
                if (selectedPotential.splend_initiative === SplendInitiative.DynamicPricing) {
                    setSteps(flexiDynamicSteps);
                    setStepNames(flexiDynamicStepNames);
                    break;
                }
                else {
                    // This is the steps followed by the Ignition program as default if no initiative is set
                    setSteps(flexiSteps);
                    setStepNames(flexiStepNames);
                    break;
                }
            case PlanOptions.SplendFlexiOwn:
                setSteps(flexiOwnSteps);
                setStepNames(flexiOwnStepNames);
                break;
            case PlanOptions.SplendRentToOwnUsedVehicle:
                setSteps(flexiOwnUsedSteps);
                setStepNames(flexiOwnUsedStepNames);
                break;
            case PlanOptions.UKFlexi:
                if (selectedPotential.splend_initiative === SplendInitiative.Ignition) {
                    setSteps(flexiIgnitionSteps);
                    setStepNames(flexiIgnitionStepNames);
                    break;
                } else if (selectedPotential.splend_initiative === SplendInitiative.DynamicPricing) {
                    setSteps(flexiDynamicStepsUK);
                    setStepNames(flexiDynamicStepNamesUK);
                    break;
                }
                else {
                    console.error("Initiative is not set. Using Flexi Ignition UK");
                    setSteps(flexiIgnitionSteps);
                    setStepNames(flexiIgnitionStepNames);
                    break;
                }
            case PlanOptions.UKFlexiOwn:
                // Check if this is the HP Car Only Promo journey
                if (contractDetails.isHPCarOnly) {
                    setSteps(hpCarOnlyPromoSteps);
                    setStepNames(hpCarOnlyPromoStepNames);
                } else {
                    setSteps(flexiOwnUKSteps);
                    setStepNames(flexiOwnUKStepNames);
                }
                break;
            case PlanOptions.UKRentToOwnUsed:
                setSteps(flexiOwnUsedUKSteps);
                setStepNames(flexiOwnUsedUKStepNames);
                break;
        }
    };

    // Checks the value of the plan in the User Context
    useEffect(() => {
        setPlan(driverDetails.plan);
        selectJourneyFromPlan(driverDetails.plan as PlanOptions);
    }, [plan]);

    // Check to see if the plan will require an invoice preview
    const isPlanRequireInvoicePreview = (plan: string) => {
        return !(plan === PlanOptions.RAVCreditHire || plan === PlanOptions.RAVSharecover)
    }

    useEffect(() => {
        if (isPlanRequireInvoicePreview(driverDetails.plan)) {
            setInvoicePreviewRequired(true)
        }}, [driverDetails.plan])

    // The only purpose of this hook is to check whether a value that is required to progress through the onboarding form is present.
    // Country is being used as a proxy to see if the user is able to progress with the form, the value or type of country can change
    // and this will remain functional but there has to be a value for country present for this hook to work. If a page is refreshed then
    // the information in the context will be wiped, this hook will then see that change and redirect the user back to the homepage to
    // restart the journey.
    useEffect(() => {
        if (!driverDetails.country) {
            window.location.href = "/";
        }
    }, []);

    useEffect(() => {
        addBeforeUnloadListener(); // add event listener when starting the form
        return () => {
            removeBeforeUnloadListener(); // end event listener at the end of the form
        };
    }, []);

    // Checks email pulled from Zoho for null or invalid entries. Email length <= 4 has been
    // left in on purpose as a quick check to fix production issue but will need to be 
    // expanded on in the future
    const checkValidEmailAddress = () => personalInfo.email && personalInfo.email.length >= 4;

    const {
        currentStepIndex,
        step,
        isFirstStep,
        isLastStep,
        back,
        next
    } = useMultiStepForm(steps);

    const hasNSWIncentiveData = (): boolean => {
        // If there is a cbpOnboard.PlanId, then use the appropriate field to check for NSW incentive.
        if (cbpOnboard.PlanId) {
            return !!(contractDetails.NSWWeeklyIncentiveExclGST) || !!(contractDetails.NSWTotalIncentiveExclGST);
        }
        return !!(contractDetails.NSWIncentiveTotalValue) || !!(contractDetails.NSWIncentivePlanBeforeDiscount);
    };

    const isPenultimateStep = () =>
        currentStepIndex == steps.length - 3;

    //Checks if this form should be the last step
    // Usually this returns isLastStep, but some overrides exist for addendum pages that depend on the data input during the form
    //Steps that have "optional" steps:
    // * FlexiOwn (AU)
    //     * NSW EV Addendum
    // * FlexiOwnUsed (AU)
    //     * NSW EV Addendum
    const checkIsLastStep = () => {
        // If:
        // We are on the penultimate step (might be the final step)
        // & of a journey with an optional NSWEV addendum,

        // Separating the handling of FlexiOwn and FlexiOwnUsed because:
        // - FlexiOwnUsed may end with the NSW EV step.
        // - FlexiOwn always ends with the IPB step instead, and may or may not include NSW EV step.
        if (driverDetails.plan == PlanOptions.SplendRentToOwnUsedVehicle) {
            const currentStepIsUsedVehicleAddendum =
                stepNames[currentStepIndex] == 'Splend Flexi Own - Used vehicle';
        
            const nextStepIsNSWAddendum =
                stepNames[currentStepIndex + 1] == 'NSW EV Addendum';
        
            // If we're currently on the Used Vehicle Addendum step:
            if (currentStepIsUsedVehicleAddendum) {
                // If NSW incentive data is present, go to the NSW EV Addendum
                if (hasNSWIncentiveData()) {
                    return false; // not last step — go to NSW Addendum
                } else {
                    return true; // no NSW data — this is the last step
                }
            }
        
            // If we are currently on NSW Addendum, and it was reached, it must be the last step
            if (nextStepIsNSWAddendum) {
                return false;
            }
        }
        else if (JSON.stringify(stepNames) == JSON.stringify(flexiOwnStepNames)) {
            // If currently on Agreement Schedule step of FlexiOwn journey, check if
            // NSW EV step should be displayed or skipped.
            if (isPenultimateStep() && !hasNSWIncentiveData()) {
                next();
            }
        }

        // If not a journey with optional last step, then just return isLastStep
        return isLastStep;
    };

    // Handles clicking the previous button.
    // This extends the `back` function so that the previous button can handle
    // going from the IPB step back to the Agreement Schedule. This ensures that
    // the NSW Incentive step is skipped if the subscription is not applicable.
    // By default, it goes back one step.
    const goToPreviousStep = () => {
        if (displayReviewPage) {
            setDisplayReviewPage(false);
            // setDisplayInvoicePreviewPage(false);  //
            return;
        }

        if (displayInvoicePreviewPage) {
            setDisplayInvoicePreviewPage(false);
            setDisplayReviewPage(true);
            return;
        }

        // Ensure the skipping of NSW Incentive step applies only to FlexiOwn AU,
        // preventing unintended step adjustments for FlexiOwn UK.
        if (
            stepNames.includes("NSW EV Addendum")
            && !hasNSWIncentiveData()
            && (currentStepIndex == steps.length - 1)
        ) {
            back(2);
        } else {
            back();
        }
    }

    useEffect(
        () => {
            if (displayReviewPage && invoicePreviewRequired) {
                setSubmitButtonText('Preview invoice')
            }
            else if (displayReviewPage) {
                setSubmitButtonText('Create subscription')
            }
            else if (displayInvoicePreviewPage) {
                // this will change to create subscription and invoice when auto invoicing turns on
                setSubmitButtonText('Create subscription')
            }
            else {
                setSubmitButtonText('Next')
            }
        },
        [displayReviewPage, displayInvoicePreviewPage]
    );

    const onSubmitForm = (event: FormEvent) => {
        // removeBeforeUnloadListener(); // end event listener at the end of the form

        event.preventDefault();

        // if the email address fails validation then display error and redirect to homepage
        if (!checkValidEmailAddress()) {
            setErr(
                true,
                {
                    msg: 'Please enter a valid email address on Zoho and try again',
                    UI: <InvalidEmailWarningModal onClickClose={() => { removeErr(); navigate('/'); }} />
                }
            );
        }

        // HP Car Only Promo: Check if document upload is complete before allowing progression from first step
        if (contractDetails.isHPCarOnly && currentStepIndex === 0 && !contractDetails.documentUploadComplete) {
            setErr(
                true,
                {
                    msg: 'Please upload the required insurance document before continuing',
                    UI: <Warning
                        message="Please upload the required insurance document before continuing"
                        additionalInfo="All documents must be successfully uploaded to proceed with the onboarding."
                        onClickClose={() => removeErr()}
                    />
                }
            );
            return;
        }

        // if it's not the last step, then go to the next one
        if (checkIsLastStep() === false) { next(); return; }

        // If it get past by previous if, then it was the last step, so the
        // next screen should be the Review, then Invoice Preview. It is implemented in this way
        // because the Review and Invoice Preview component are not part of the steps - multiform
        // mechanism.

        // If neither are set to true, then we display Review
        if (!displayReviewPage && !displayInvoicePreviewPage) {  // To display Invoice Preview, we need to hide Review
            setDisplayReviewPage(true);
            return;
        }

        if (invoicePreviewRequired && !displayInvoicePreviewPage) {
            setDisplayReviewPage(false);
            setDisplayInvoicePreviewPage(true);
            return;
        }

        setLoading(true);

        const payload = createPayload(
            driverDetails.plan,
            driverDetails,
            personalInfo,
            carInfo,
            contractDetails,
            addressInfo,
            selectedPotential,
            emergencyContactInfo,
            usedVehicleAddendum,
            splendInitiatives,
            cbpOnboard
        );
        console.log('Onboarding payload: ', payload);

        OnboardingService.obtainCustomerSignUrl(
            payload,
            Country[newCountry],
            { msalContext, isAuthenticated }
        ).then((resp) => {
            removeBeforeUnloadListener(); // end event listener at the end of the form
            // go to signing page
            window.location.replace(resp.sign_url);
            setLoading(false);
        }).catch((err) => {
            // Display an error modal - by acknowledging the error and clicking Ok btn, the user is sent to homepage
            setErr(
                true,
                {
                    msg: 'Error with onboarding.',
                    UI: <WarningModal onClickClose={() => { removeErr(); navigate('/'); }} />
                }
            );
            setLoading(false);
        });
    };

    // For sending only the breadcrumbs related to the page to Sentry
    const getComponentName = (stepIndex: any, driverDetails: any) => {
        switch (driverDetails.plan) {
            case PlanOptions.RAVCreditHire:
                return ravStepNames[stepIndex];
            case PlanOptions.RAVSharecover:
                return ravStepNames[stepIndex];
            case PlanOptions.RAVShortTermRental:
                return ravShortTermStepNames[stepIndex];
            case PlanOptions.SplendFlexi:
                return flexiStepNames[stepIndex];
            case PlanOptions.SplendFlexiOwn:
                return flexiOwnStepNames[stepIndex];
            case PlanOptions.SplendRentToOwnUsedVehicle:
                return flexiOwnUsedStepNames[stepIndex];
        }
    };

    const ReviewPage = () => {
        // The check currently relies on the fact that there can't be
        // any UK journey to fill it and neither an AU onboarding
        // apart from FOv2. As such, the component was customised for it.
        if (cbpOnboard.PlanId){
            return (
                <OnboardingV2Review
                    carInfo={carInfo}
                    addressInfo={addressInfo}
                    contractDetails={contractDetails}
                    driverDetails={driverDetails}
                    personalInfo={personalInfo}
                    emergencyContactInfo={emergencyContactInfo}
                    cbpOnboard={cbpOnboard}
                />);
        }
        return (
            <OnboardingReview
                carInfo={carInfo}
                addressInfo={addressInfo}
                contractDetails={contractDetails}
                driverDetails={driverDetails}
                personalInfo={personalInfo}
                emergencyContactInfo={emergencyContactInfo}
                usedVehicleAddendum={usedVehicleAddendum}
                splendInitiatives={splendInitiatives}
                selectedPotential={selectedPotential} 
                cbpOnboard={cbpOnboard}
            />);
    };

    const InvoicePreviewPage = useMemo(() => () => (
        <InvoicePreview
            country={driverDetails.country}
            hub={carInfo.hub}
            customerId={driverDetails.crmId}
            customerEmail={personalInfo.email}
            planId={cbpOnboard.PlanId != "" ? cbpOnboard.PlanId : contractDetails.planId}
            planName={contractDetails.planName}
            subscriptionStartDate={contractDetails.subsStartDate}
            dynamic={splendInitiatives.totalDynamicPricing}
            ignition={splendInitiatives.additionalDiscount}
            ipb={contractDetails.ipbDiscountAmount}
            // Structure that will need adjusted? as this is coupled to
            // the structure stored by context
            addons={cbpOnboard.quoteAddons} />
    ), [driverDetails.country, carInfo.hub, driverDetails.crmId, contractDetails.planId, contractDetails.planName, contractDetails.subsStartDate, splendInitiatives.totalDynamicPricing, splendInitiatives.additionalDiscount, contractDetails.ipbDiscountAmount]);

    const getPage = () => {
        if ( displayReviewPage ) {
            return <ReviewPage />
        }
        else if ( displayInvoicePreviewPage ) {
            return <InvoicePreviewPage />
        }
        else {
            return (
                <>
                    <p><strong>Step {currentStepIndex + 1}</strong></p>
                    {step}
                </>
            )
        }
    }

    const getPageTitle = () => {
        if ( displayReviewPage ) {
            return 'Review'
        }
        else if ( displayInvoicePreviewPage ) {
            return 'Invoice Preview'
        }
        else {
            return stepNames[currentStepIndex]
        }
    }

    return (<>
        <NavBar
            pageTitle={getPageTitle()}
            backAction={!isFirstStep ? goToPreviousStep : undefined}
        />
        <div style={{ margin: '2.5vh 2.5vw' }}>
            <form onSubmit={onSubmitForm}>
                <div style={{ top: '.5rem', right: '.5rem' }}>
                    {getPage()}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5vh 0' }}>
                    <SubmitBtn
                        testId={"onboarding-submit-button"}
                        text={submitButtonText}
                        breadcrumbObject={breadCrumbObject}
                        componentName={getComponentName(currentStepIndex, driverDetails)}
                    />
                </div>
            </form>
        </div>
        {loading && <ForegroundLoadingSpinner />}
    </>);
};


const WarningModal = ({ onClickClose }: { onClickClose: React.MouseEventHandler }) => (
    <Warning
        message="Something went wrong"
        additionalInfo={`We’re sorry, we were unable to process your request.  
        We will investigate the issue and in the meantime, please onboard the customer via an alternative method.`}
        onClickClose={onClickClose}
    />
);


const ForegroundLoadingSpinner = () => (
    <ModalContainer stylesCfg={{ windowTransparency: true }} >
        <div style={{ display: 'flex', justifyContent: 'center', margin: '15vh' }}>
            <CircularSpinner />
        </div>
    </ModalContainer>
);


const InvalidEmailWarningModal = ({ onClickClose }: { onClickClose: React.MouseEventHandler }) => (
    <Warning
        message="Invalid Email Address Detected"
        additionalInfo={`Please enter a valid email address on Zoho and try again.`}
        onClickClose={onClickClose}
    />
);
